import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';
import type { SavedMeeting, MeetingAttendance, ServicePosition } from '../../types/meetings';

// ── Saved Meetings ──────────────────────────────────────────────────────────

export async function getSavedMeetings(uid: string): Promise<SavedMeeting[]> {
  const snap = await getDocs(
    query(collection(db, `users/${uid}/savedMeetings`), orderBy('name'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SavedMeeting));
}

export async function addSavedMeeting(
  uid: string,
  data: Omit<SavedMeeting, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const ref = await addDoc(collection(db, `users/${uid}/savedMeetings`), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateSavedMeeting(
  uid: string,
  meetingId: string,
  data: Partial<Omit<SavedMeeting, 'id' | 'createdAt'>>
): Promise<void> {
  await updateDoc(doc(db, `users/${uid}/savedMeetings/${meetingId}`), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteSavedMeeting(uid: string, meetingId: string): Promise<void> {
  await deleteDoc(doc(db, `users/${uid}/savedMeetings/${meetingId}`));
}

// ── Meeting Attendance ──────────────────────────────────────────────────────

export async function getAttendanceHistory(
  uid: string,
  limitCount = 50
): Promise<MeetingAttendance[]> {
  const snap = await getDocs(
    query(
      collection(db, `users/${uid}/meetingAttendance`),
      orderBy('attendedDate', 'desc'),
      limit(limitCount)
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as MeetingAttendance));
}

export async function logAttendance(
  uid: string,
  data: Omit<MeetingAttendance, 'id' | 'createdAt'>
): Promise<string> {
  const ref = await addDoc(collection(db, `users/${uid}/meetingAttendance`), {
    ...data,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteAttendance(uid: string, attendanceId: string): Promise<void> {
  await deleteDoc(doc(db, `users/${uid}/meetingAttendance/${attendanceId}`));
}

// ── Service Positions ───────────────────────────────────────────────────────

export async function getServicePositions(uid: string): Promise<ServicePosition[]> {
  const snap = await getDocs(
    query(collection(db, `users/${uid}/servicePositions`), orderBy('startDate', 'desc'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ServicePosition));
}

export async function addServicePosition(
  uid: string,
  data: Omit<ServicePosition, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const ref = await addDoc(collection(db, `users/${uid}/servicePositions`), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateServicePosition(
  uid: string,
  positionId: string,
  data: Partial<Omit<ServicePosition, 'id' | 'createdAt'>>
): Promise<void> {
  await updateDoc(doc(db, `users/${uid}/servicePositions/${positionId}`), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deactivateServicePosition(
  uid: string,
  positionId: string,
  endDate: string
): Promise<void> {
  await updateDoc(doc(db, `users/${uid}/servicePositions/${positionId}`), {
    isActive: false,
    endDate,
    updatedAt: serverTimestamp(),
  });
}
